import { useFeatureFlag, useIsMobile } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useMemo } from 'react';

import { ChatContainer } from '../components/chat-container';
import { ChatContent } from '../components/chat-content';
import {
  AccountSelector,
  ChatHeader,
  ChatHeaderActions,
  ChatMinimizeButton,
  NewChatButton,
  ToggleSidebar,
} from '../components/chat-header';
import { ChatRoot } from '../components/chat-root';
import {
  ChatSidebar,
  ChatSidebarFilterTabs,
  ChatSidebarHistory,
  ChatSidebarNewChat,
  ChatSidebarSearch,
  ChatSidebarSettingsButton,
} from '../components/chat-sidebar';
import { ArtifactPreviewProvider } from '../components/tools/artifact-preview/artifact-preview-provider';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { createDefaultDataPartRenderers, createDefaultToolRenderers } from '../lib/default-renderers';
import { NovaChatProvider } from '../providers';
import { NavigatorGraphPayload } from './navigator-graph-payload';

/**
 * Container component that renders the chat UI.
 * Reads all props from context - no prop drilling needed.
 * All child components are self-contained and manage their own state.
 */
export function NovaChatContainer() {
  const {
    isVisible,
    authProvider,
    apiEndpoint,
    userUuid,
    accountUuid,
    userScopes: scopes,
    chatUuid,
    initialMessages,
    isLoadingMessages: loading,
    placeholder,
    isStandaloneMode,
    isSidebarOpen,
    isFullscreen,
    isSettingsOpen,
  } = useChatOverlayContext();
  const isNovaSourcesRenderingEnabled = useFeatureFlag('nova-sources-rendering', false);
  const { isMobile } = useIsMobile();
  const desktopDockedSidebarClasses = !isMobile && !isFullscreen ? 'rounded-tr-sm rounded-tl-none' : undefined;
  const chatFrameClassName = cn('flex h-full min-w-0 flex-1 flex-col transition-[margin-left] duration-200', {
    'ml-[288px]': (isStandaloneMode || isFullscreen) && isSidebarOpen && !isMobile,
    'ml-[63px]': (isStandaloneMode || isFullscreen) && !isSidebarOpen && !isMobile,
  });

  // Create default renderers (memoized to avoid recreating on every render)
  const toolRenderers = useMemo(() => createDefaultToolRenderers(), []);
  const dataPartRenderers = useMemo(() => createDefaultDataPartRenderers(), []);

  return (
    <ChatContainer isVisible={isVisible} className={desktopDockedSidebarClasses}>
      <NovaChatProvider
        authProvider={authProvider}
        userUuid={userUuid}
        accountUuid={accountUuid}
        apiEndpoint={apiEndpoint}
        scopes={scopes}
        initialMessages={initialMessages}
      >
        <NavigatorGraphPayload />
        <ChatSidebar>
          <ChatSidebarNewChat />
          <ChatSidebarSettingsButton />
          <ChatSidebarSearch />
          <ChatSidebarFilterTabs />
          <ChatSidebarHistory />
        </ChatSidebar>
        <ChatRoot key={chatUuid}>
          <div className={chatFrameClassName}>
            <ChatHeader className={desktopDockedSidebarClasses}>
              {isSettingsOpen ? (
                <>
                  {isMobile ? <ToggleSidebar /> : null}
                  <h2 className="text-muted-foreground text-[17px] leading-6 font-normal">Settings</h2>
                  <ChatHeaderActions />
                </>
              ) : isMobile ? (
                <>
                  <ToggleSidebar className="shrink-0" />
                  <div className="flex min-w-0 flex-1 justify-center px-2">
                    <AccountSelector className="min-w-0 truncate" />
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <NewChatButton />
                    <ChatMinimizeButton />
                  </div>
                </>
              ) : (
                <>
                  <AccountSelector />
                  <ChatHeaderActions />
                </>
              )}
            </ChatHeader>
            <ArtifactPreviewProvider className="min-h-0 min-w-0 flex-1" contentClassName="size-full min-w-0">
              <ChatContent
                isLoading={loading}
                toolRenderers={toolRenderers}
                dataPartRenderers={dataPartRenderers}
                placeholder={placeholder}
                enableSourcesRendering={isNovaSourcesRenderingEnabled}
                withArtifactPreviewProvider={false}
              />
            </ArtifactPreviewProvider>
          </div>
        </ChatRoot>
      </NovaChatProvider>
    </ChatContainer>
  );
}
